// apps/dashboard/src/api/hooks/useStartRefinement.ts
import { useMutation } from "convex/react";
import { ConvexError } from "convex/values";
import { api } from "../../../../../convex/_generated/api";
import { useState, useCallback } from "react";
import type { Id } from "../../../../../convex/_generated/dataModel";
import type { ApiError } from "../types";

interface StartArgs {
  briefId:             Id<"briefAnalyses">;
  repositoryId:        Id<"repositories">;
  maxIterations:       number;
  contentThreshold:    number;
  structuralThreshold: number;
}

export function useStartRefinement() {
  const mutate = useMutation(api.workflowRuns.startRefinement);

  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [lastRunId, setLastRunId] = useState<Id<"workflowRuns"> | null>(null);

  const start = useCallback(async (args: StartArgs) => {
    setIsStarting(true);
    setError(null);
    try {
      const result = await mutate(args);
      setLastRunId(result.runId);
    } catch (err: unknown) {
      if (err instanceof ConvexError) {
        // ConvexError: application-level error from the wrapper or from the inner
        // mutation. data.retryAfterMs is preserved end-to-end for RATE_LIMIT.
        setError({
          code:         (err.data?.code as string)    ?? "UNKNOWN",
          message:      err.data?.message as string | undefined, // eslint-disable-line @typescript-eslint/no-unnecessary-type-assertion
          retryAfterMs: err.data?.retryAfterMs as number | undefined,
        });
      } else {
        // Non-ConvexError. The backend's E.error() helper (convex/helpers/errors.ts)
        // throws plain Error objects whose `.code`/`.extra` properties don't
        // survive Convex's wire protocol — only the message string does. The
        // server prefixes it with "Uncaught Error: " (sometimes nested) and adds
        // a "[CONVEX M(…)] [Request ID: …] Server Error\n" envelope.
        //
        // Strip the envelope and surface the real message when we can; fall
        // back to "Network error" only when the message is unrecognisable.
        console.error("[useStartRefinement] non-ConvexError exception:", err); // eslint-disable-line no-console
        let surfaceMessage = "Network error. Please try again.";
        if (err instanceof Error && err.message) {
          const inner = err.message
            .replace(/^\[CONVEX[^\n]*Server Error[\r\n]+/i, "")
            .replace(/^(?:Uncaught (?:Error|ConvexError):\s*)+/i, "")
            .replace(/\n\s*Called by client[\s\S]*$/m, "")
            .trim();
          if (inner.length > 0 && inner.length < 240) {
            surfaceMessage = inner;
          }
        }
        setError({
          code:    "PLATFORM_ERROR",
          message: surfaceMessage,
        });
      }
    } finally {
      setIsStarting(false);
    }
  }, [mutate]);

  return { start, isStarting, error, lastRunId };
}
